import { TextInput } from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { ChangeEvent, useCallback } from 'react';

import { Address } from '@generated/gql/graphql';
import { useNestedFieldProps } from '@hooks/useNestedFieldProps';

import { NestedFormValues } from '../types';
import { getAddressFieldValues } from '../utils';

interface AddressManualEntryProps<T> {
  formName: T;
  isLoading: boolean;
  onChange?(addressSelection: Address): void;
}

// ensure the formName is removed from the field name. Since values
// retrieved from the Formik state do this, perhaps due to storing
// the form state differently, new changes must fix this manually
// as they are added into the resulting values object directly
// using the `target.name` field, which contains the form name prefix.
const getSanitizedFieldName = (field: string, formName: string) => {
  const hasPrefix = new RegExp(`${formName}..+`).test(field);
  if (!hasPrefix) return field;

  return field.split('.')[1];
};

const AddressManualEntry = <T extends string>({
  formName,
  isLoading,
  onChange,
}: AddressManualEntryProps<T>) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  }: FormikProps<NestedFormValues<T>> = useFormikContext();

  const handleFieldChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleChange(event);

      const addressFieldValues = getAddressFieldValues(values[formName]);

      onChange &&
        onChange({
          ...addressFieldValues,
          [getSanitizedFieldName(event.target.name, formName)]:
            event.target.value,
        } as Address);
    },
    [handleChange, onChange, values, formName]
  );
  const addressFieldValues = getAddressFieldValues(values[formName]);

  const getFieldProps = useNestedFieldProps<NestedFormValues<string>, string>({
    values,
    errors,
    handleChange: handleFieldChanged,
    handleBlur,
    touched,
    submitCount,
    formName,
    isLoading,
  });

  return (
    <>
      <TextInput
        label="Flat number"
        {...getFieldProps('subBuilding')}
        data-testid="subBuilding"
      />
      <TextInput
        label="Building name"
        {...getFieldProps('buildingName')}
        data-testid="buildingName"
        required={!addressFieldValues.buildingNumber}
      />
      <TextInput
        label="Building number"
        {...getFieldProps('buildingNumber')}
        data-testid="buildingNumber"
        required={!addressFieldValues.buildingName}
      />
      <TextInput
        label="Street"
        {...getFieldProps('thoroughfare')}
        data-testid="thoroughfare"
        required
      />
      <TextInput
        label="City/Town"
        {...getFieldProps('postTown')}
        data-testid="postTown"
        required
      />
      <TextInput
        label="Postcode"
        {...getFieldProps('postcode')}
        data-testid="AddressPickerPostCode"
        required
      />
    </>
  );
};

export { AddressManualEntry };
